import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_API, GET_ALL_VENDORS_API, GET_NEW_INWARD_BY_ID_API, UPDATE_INWARD_API } from '../config/API';
import { MdArrowBack, MdCloudUpload, MdCheckCircle, MdPhotoCamera } from 'react-icons/md';
import Swal from 'sweetalert2';
import Select from 'react-select';
import PaginatedSelect from '../components/common/PaginatedSelect';
import Loader from '../components/common/Loader';
import './InwardEntry.css';
import { formatDate } from '../utils/dateUtils';

const EditInward = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Data States
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [selectedPo, setSelectedPo] = useState(null);
    const [poData, setPoData] = useState(null);
    const [inwardNo, setInwardNo] = useState('');
    const [items, setItems] = useState([]);
    const [challanFile, setChallanFile] = useState(null);
    const [challanPreview, setChallanPreview] = useState(null);
    const [remarks, setRemarks] = useState('');
    const [initialData, setInitialData] = useState(null);
    const [uploadMethod, setUploadMethod] = useState('file'); // 'file' or 'camera'

    useEffect(() => {
        const fetchInwardDetails = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const headers = { Authorization: `Bearer ${token}` };


                // 2. Fetch Inward Details
                const inwardRes = await axios.get(GET_NEW_INWARD_BY_ID_API(id), { headers });
                if (inwardRes.data.status) {
                    const inwardData = inwardRes.data.data;
                    setInitialData(inwardData);
                    setInwardNo(inwardData.inwardNo || `#${inwardData.id}`);
                    setRemarks(inwardData.remarks || '');
                    setPoData(inwardData); // Use inward data for general details (project, etc.)

                    if (inwardData.challanFile) {
                        setChallanPreview(inwardData.challanFile.startsWith('https') ? inwardData.challanFile : `${BASE_API}/uploads/challans/${inwardData.challanFile}`);
                    }

                    // Set Vendor
                    if (inwardData.Vendor) {
                        setSelectedVendor({
                            value: inwardData.vendorId,
                            label: `${inwardData.Vendor.vendorName} (${inwardData.Vendor.companyName || 'N/A'})`
                        });
                    }

                    // Set PO Placeholder (Read Only in Edit)
                    setSelectedPo({
                        value: inwardData.purchaseOrderId,
                        label: inwardData.PurchaseOrder?.poNumber || `#${inwardData.purchaseOrderId || 'N/A'}`
                    });

                    // Map items from InwardItems directly from the response
                    if (inwardData.InwardItems) {
                        setItems(inwardData.InwardItems.map(item => ({
                            ...item,
                            productName: item.Product?.productName || `Product ID: ${item.productId}`,
                            orderedQty: Number(item.orderedQty) || 0,
                            receivedTotal: Number(item.totalReceivedAllInwards || item.receivedTotal) || 0,
                            pendingQty: Number(item.pendingQuantityOverall || item.pendingQty || 0),
                            previousTotalReceived: Number(item.previousTotalReceived) || 0,
                            receiveNow: item.receiveNow || 0,
                            unitName: item.Unit?.unitName || 'Nos'
                        })));
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchInwardDetails();
    }, [id]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setChallanFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setChallanPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveFile = () => {
        setChallanFile(null);
        setChallanPreview(null);
        const fileInput = document.getElementById('challan-upload');
        const cameraInput = document.getElementById('camera-upload');
        if (fileInput) fileInput.value = '';
        if (cameraInput) cameraInput.value = '';
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const handleSubmit = async () => {
        const result = await Swal.fire({
            title: 'Update Inward Entry?',
            text: `Confirming changes for Inward ${inwardNo}`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update',
            confirmButtonColor: '#6366f1'
        });

        if (result.isConfirmed) {
            setSubmitting(true);
            try {
                const token = localStorage.getItem('accessToken');
                const formData = new FormData();

                formData.append('remarks', remarks);

                if (challanFile) {
                    formData.append('challanFile', challanFile);
                } else if (challanPreview && typeof challanPreview === 'string' && !challanPreview.startsWith('data:')) {
                    // Send existing filename/URL if not changed
                    formData.append('challanFile', initialData?.challanFile || "");
                } else {
                    formData.append('challanFile', "");
                }

                formData.append('inwardItems', JSON.stringify(items.map(item => ({
                    id: Number(item.id),
                    orderedQty: Number(item.orderedQty),
                    receivedTotal: Number(item.receivedTotal),
                    receiveNow: Number(item.receiveNow)
                }))));

                const response = await axios.put(UPDATE_INWARD_API(id), formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response.data.status) {
                    Swal.fire('Success!', 'Inward entry updated successfully.', 'success').then(() => {
                        navigate('/inward-management');
                    });
                } else {
                    Swal.fire('Error', response.data.error || response.data.message || 'Failed to update inward.', 'error');
                }
            } catch (error) {
                console.error('Error updating inward:', error);
                Swal.fire('Error', error.response?.data?.error || 'Failed to update inward.', 'error');
            } finally {
                setSubmitting(false);
            }
        }
    };



    return (
        <div className="inward-entry-page">
            <div className="page-header">
                <button className="back-btn" onClick={() => navigate('/inward-management')}>
                    <MdArrowBack size={20} />
                </button>
                <h2>Edit Inward Entry: {inwardNo}</h2>
            </div>

            <div className="inward-form-card">
                <form onSubmit={(e) => e.preventDefault()}>
                    <div className="form-section">
                        <h3>General Details</h3>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Vendor</label>
                                <PaginatedSelect
                                    apiUrl={GET_ALL_VENDORS_API}
                                    mapResponse={(v) => ({
                                        value: v.id || v._id,
                                        label: `${v.vendorName} (${v.companyName || 'N/A'})`,
                                        allData: v
                                    })}
                                    value={selectedVendor}
                                    isDisabled={true}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div className="form-group">
                                <label>Purchase Order</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={selectedPo?.label || ''}
                                    disabled
                                />
                            </div>
                            <div className="form-group">
                                <label>PO Date</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formatDate(poData?.date)}
                                    disabled
                                />
                            </div>
                            <div className="form-group">
                                <label>Project Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={
                                        poData?.ProjectMaster?.projectName ||
                                        poData?.Project?.projectName ||
                                        'N/A'
                                    }
                                    disabled
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Delivery Challan & Details</h3>
                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label>Change Challan Document</label>

                                <div className="upload-tabs">
                                    <button
                                        type="button"
                                        className={`tab-btn ${uploadMethod === 'file' ? 'active' : ''}`}
                                        onClick={() => setUploadMethod('file')}
                                    >
                                        <MdCloudUpload size={18} /> Upload File
                                    </button>
                                    <button
                                        type="button"
                                        className={`tab-btn ${uploadMethod === 'camera' ? 'active' : ''}`}
                                        onClick={() => setUploadMethod('camera')}
                                    >
                                        <MdPhotoCamera size={18} /> Take Photo
                                    </button>
                                </div>

                                <div
                                    className={`challan-upload-zone ${uploadMethod === 'camera' ? 'active-camera' : ''}`}
                                    onClick={() => {
                                        if (uploadMethod === 'file') {
                                            document.getElementById('challan-upload').click();
                                        } else {
                                            document.getElementById('camera-upload').click();
                                        }
                                    }}
                                >
                                    <input
                                        type="file"
                                        accept="image/*,.pdf"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                        id="challan-upload"
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                        id="camera-upload"
                                    />
                                    <div className="upload-icon-wrapper">
                                        {uploadMethod === 'file' ? (
                                            <>
                                                <MdCloudUpload size={48} color="#6366f1" />
                                                <span className="upload-text-primary">
                                                    {challanFile ? challanFile.name : (challanPreview ? 'Click to Change Challan' : 'Click to Upload Challan')}
                                                </span>
                                                <span className="upload-text-secondary">Supports JPG, PNG, PDF</span>
                                            </>
                                        ) : (
                                            <>
                                                <MdPhotoCamera size={48} color="#10b981" />
                                                <span className="upload-text-primary" style={{ color: '#065f46' }}>
                                                    {challanFile ? 'Photo Captured' : 'Click to Open Camera'}
                                                </span>
                                                <span className="upload-text-secondary">Selfie or Document Scan</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {challanPreview && typeof challanPreview === 'string' && (
                                    <div className="preview-container">
                                        <div className="preview-header">
                                            <span className="preview-label">File Preview:</span>
                                            <div className="preview-actions">
                                                <button
                                                    type="button"
                                                    className="preview-action-btn view-full-btn"
                                                    onClick={() => window.open(challanPreview, '_blank')}
                                                >
                                                    View Full Screen
                                                </button>
                                                <button
                                                    type="button"
                                                    className="preview-action-btn remove-file-btn"
                                                    onClick={handleRemoveFile}
                                                >
                                                    Remove File
                                                </button>
                                            </div>
                                        </div>
                                        {(challanFile?.type?.startsWith('image/') || (typeof challanPreview === 'string' && (challanPreview.startsWith('data:image') || challanPreview.match(/\.(jpg|jpeg|png)$/i)))) ? (
                                            <img src={challanPreview} alt="Preview" className="challan-preview-img" />
                                        ) : (challanFile?.type === 'application/pdf' || (typeof challanPreview === 'string' && (challanPreview.startsWith('data:application/pdf') || challanPreview.match(/\.pdf$/i)))) ? (
                                            <div className="pdf-preview-wrapper">
                                                <iframe
                                                    src={challanPreview}
                                                    title="Challan PDF Preview"
                                                    className="challan-pdf-iframe"
                                                ></iframe>
                                            </div>
                                        ) : (
                                            <div className="challan-file-badge">Challan File Uploaded</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="form-group full-width">
                                <label>Remarks / Notes</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Items Verification</h3>
                        <div className="items-table-wrapper">
                            <table className="items-table">
                                <thead>
                                    <tr>
                                        <th>Item Name</th>
                                        <th>Ordered Qty</th>
                                        <th>Received (Total)</th>
                                        <th>Pending Qty</th>
                                        <th style={{ width: '100px' }}>Receive (Now)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, index) => (
                                        <tr key={index}>
                                            <td className="item-name-cell">{item.productName}</td>
                                            <td className="qty-display">
                                                {Number(item.orderedQty || 0).toFixed(2)} {item.unitName}
                                            </td>
                                            <td>{Number(item.previousTotalReceived || 0).toFixed(2)}</td>
                                            <td>{Number(item.orderedQty - item.previousTotalReceived || 0).toFixed(2)}</td>
                                            <td className="receive-now-cell">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="form-control"
                                                    value={item.receiveNow}
                                                    onChange={(e) => handleItemChange(index, 'receiveNow', e.target.value)}
                                                    max={item.pendingQty || 0}
                                                />
                                                <span className="unit-label">{item.unitName}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile View */}
                        <div className="mobile-items-view">
                            {items.map((item, index) => (
                                <div key={index} className="mobile-item-card">
                                    <div className="mobile-item-header">
                                        <div className="mobile-item-title">
                                            <span className="sr-badge">Item {index + 1}</span>
                                            <strong>{item.productName}</strong>
                                        </div>
                                    </div>
                                    <div className="mobile-item-body">
                                        <div className="mobile-item-row">
                                            <span className="m-label">Ordered Qty</span>
                                            <span className="m-value">{item.orderedQty} {item.unitName || 'Nos'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Received Total</span>
                                            <span className="m-value">{item.previousTotalReceived || 0} {item.unitName || 'Nos'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Pending Qty</span>
                                            <span className="m-value">{Number(item.orderedQty - item.previousTotalReceived || 0).toFixed(2)} {item.unitName || 'Nos'}</span>
                                        </div>
                                        <div className="mobile-item-row full-width">
                                            <span className="m-label">Receive Now ({item.unitName || 'Nos'})</span>
                                            <input
                                                type="number"
                                                min="0"
                                                className="form-control"
                                                value={item.receiveNow}
                                                onChange={(e) => handleItemChange(index, 'receiveNow', e.target.value)}
                                                max={item.pendingQty || 0}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="button" className="cancel-btn" onClick={() => navigate('/inward-management')}>
                            Cancel
                        </button>
                        <button type="button" className="save-btn" onClick={handleSubmit} disabled={submitting}>
                            {submitting ? 'Updating...' : (
                                <>
                                    <MdCheckCircle size={20} /> Update Inward
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditInward;
